import { apiFetch, apiFetchEnvelope, type ListMeta } from "@core/http";
import type { components } from "@core/types/openapi";

// Alias local, no en `@core/types` — mismo criterio que
// `access-log/api.ts` (`type AccessLogEntry = components["schemas"][...]`):
// el tipo generado se consume donde nace, sin agregar una entrada más a
// `core/types/index.ts` (web-portal no toca `src/core/**` salvo el
// generado del contrato, SPEC-214 brief).
export type PortalRequest = components["schemas"]["Request"];
export type RequestStatus = NonNullable<PortalRequest["status"]>;
export type StaffUser = components["schemas"]["User"];

const base = "/v1/portal/requests";

export interface RequestFilter {
  // SPEC-214 §4: la bandeja no expone filtro por asignado ni por rango de
  // fechas (findings PA-029/PA-030, ya resueltos) — el único filtro es
  // estado, y "" significa "todas".
  status: RequestStatus | "";
}

export interface RequestsPage {
  items: PortalRequest[];
  meta: ListMeta;
}

export async function listRequests(
  filter: RequestFilter,
  cursor?: string,
): Promise<RequestsPage> {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (cursor) params.set("cursor", cursor);
  const suffix = params.size ? `?${params}` : "";
  const envelope = await apiFetchEnvelope<PortalRequest[]>(`${base}${suffix}`);
  return {
    items: envelope.data,
    meta: envelope.meta ?? { next: null, total: envelope.data.length },
  };
}

// El actor sale de la sesión del portal en el servidor (SPEC-214 §7 y
// brief de api-http): el cuerpo nunca lo manda.
export const assignRequest = (id: string, assignedTo: string) =>
  apiFetch<PortalRequest>(`${base}/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    body: { assignedTo },
  });

// SPEC-214 §8: sin responsable asignado, el servidor responde 422
// `UNASSIGNED`. Este cliente no lo anticipa con una validación propia: es
// una regla del dominio (api-domain), no un chequeo replicable acá.
export const contactRequest = (id: string) =>
  apiFetch<PortalRequest>(`${base}/${encodeURIComponent(id)}/contact`, {
    method: "POST",
  });

// QA-MTZ-002 ronda 2 #7: siempre con cuerpo. Sin él la API respondía 415
// (el contrato declara `requestBody`) y el portal mostraba un error genérico.
export const closeRequest = (id: string, note: string) =>
  apiFetch<PortalRequest>(`${base}/${encodeURIComponent(id)}/close`, {
    method: "POST",
    body: { note },
  });

// Responsables asignables para el modal de "Asignar": mismo endpoint de
// seguridad (`/v1/portal/security/users`) que ya consume
// `members/api.ts` (`listAdvisors`) para su propio selector de asesor.
// Sin el parámetro `profile` que usa esa llamada (no lo reconoce el
// contrato — ver `portalListUsers` en openapi.d.ts — RF-SOL-03 tampoco
// exige filtrar por perfil): `active=true` sí es un parámetro real.
export const listAssignableStaff = () =>
  apiFetch<StaffUser[]>("/v1/portal/security/users?active=true");
